/*
 * CHANGE STATUS… FROM THE LEADS PAGE: ONE ROW OR A SELECTION.
 *
 * Gabriel, 2026-10-26: "make it easier to take an action from here - allow me
 * to do anything that can be done to a lead on a status level." The record
 * already has every hand move, offered only where it fits (record/endings.mjs
 * changeStatusOptions) with a confirm that says what it changes
 * (record/statusConfirm.mjs). This module puts the same menu behind the row's ⋯
 * and the bulk bar, so the list and the record never offer different answers.
 *
 * ONE ROW is the record exactly: the same options, the same confirm, the same
 * writes. A SELECTION is grouped by company (status is the company's, and two
 * people at Foleon are one move), and each option is offered when it fits at
 * least one of them. The confirm counts them, names them, and says which it will
 * skip and why ("already there", "no call on record yet"), so a mixed selection
 * never half-applies in silence.
 *
 * Two corrections to the record's writes, because a Leads row is a PERSON:
 * Mark as vendor, Not a lead and Disqualify · Junk act on the people you
 * selected, where the record acts on the company's point of contact.
 *
 * And two person moves the record does not have here, at the end of the menu:
 * Mark junk (one click, as before) and Delete…, for a typo'd address or a
 * duplicate made from one (src/notALead.js deleteContact).
 *
 * Pure: the page fetches each company and its actions into `bundles`
 * ({ company, act, contacts }), and renders what these return.
 */
import { changeStatusOptions, lostReasons, writesFor, REASON_LABELS } from "./record/endings.mjs";
import { confirmSpec, confirmWrites, holdWords, dayOf } from "./record/statusConfirm.mjs";
import { STAGE_LABELS, LOST_TO_PARK_DAYS } from "./record/situation.mjs";
import { plural, listWords, fullName, longDate, addDays, toDate } from "./record/format.mjs";

const OPEN_STAGES = ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement"];

// The menu's order, whatever order the companies offered things in.
const ORDER = [...OPEN_STAGES.map((s) => `move:${s}`), "won", "hold", "not_now", "remove", "lost", "disqualify", "vendor", "not_a_lead", "churn"];
const SECTION_OF = (id) => (String(id).startsWith("move:") || id === "won" ? "move" : id === "hold" || id === "not_now" ? "hold" : "end");

// The two person moves under the status ones.
const PERSON_ITEMS = [
  { id: "junk", label: "Mark junk", hint: "spam · the record stays", confirm: "junk" },
  { id: "delete", label: "Delete…", hint: "a typo or a duplicate", confirm: "delete" },
];

// What a company IS, for "skipped: Acme (a client)".
const STATUS_WORDS = {
  prospect: "on the board",
  client: "a client",
  churned: "a former client",
  recycled: "parked",
  lost: "closed lost",
  not_a_fit: "disqualified",
  vendor: "a vendor",
  advisory: "advisory",
};

/**
 * The rows, grouped the way status works: one group per company, holding the
 * selected people at it; a person with no company is a group of their own.
 */
export function statusGroups(rows) {
  const out = [];
  const byCompany = new Map();
  for (const c of rows || []) {
    if (!c.company_id) {
      out.push({ companyId: null, contacts: [c] });
      continue;
    }
    if (!byCompany.has(c.company_id)) {
      const g = { companyId: c.company_id, contacts: [] };
      byCompany.set(c.company_id, g);
      out.push(g);
    }
    byCompany.get(c.company_id).contacts.push(c);
  }
  return out;
}

const single = (bundles) => bundles.length === 1 && bundles[0].contacts.length === 1;
const bundleName = (b) => (b.company && b.company.name) || fullName(b.contacts[0]) || "No company";
const peopleIn = (bundles) => bundles.flatMap((b) => b.contacts);

/** The record's menu for one bundle, flat: id → item. */
function offeredBy(bundle) {
  const sections = changeStatusOptions({ company: bundle.company, contact: bundle.contacts[0], act: bundle.act });
  return new Map(sections.flatMap((s) => s.items).map((i) => [i.id, i]));
}

/**
 * THE MENU: sections in changeStatusOptions' shape, then the person moves.
 * One row gets the record's own menu. A selection gets every option that fits
 * at least one company, its hint saying how many when it is not all of them.
 */
export function leadsStatusOptions(bundles) {
  const list = bundles || [];
  if (!list.length) return [];
  const person = { id: "person", label: null, items: PERSON_ITEMS };
  if (single(list)) {
    const b = list[0];
    return [...changeStatusOptions({ company: b.company, contact: b.contacts[0], act: b.act }), person];
  }

  const seen = new Map(); // id → { item, applies, enabled }
  for (const b of list) {
    for (const [id, item] of offeredBy(b)) {
      const s = seen.get(id) || { item, applies: 0, enabled: 0 };
      s.applies += 1;
      if (!item.disabled) {
        s.enabled += 1;
        if (s.item.disabled) s.item = item; // describe it by a company it works for
      }
      seen.set(id, s);
    }
  }
  const total = list.length;
  const items = ORDER.filter((id) => seen.has(id)).map((id) => {
    const { item, applies, enabled } = seen.get(id);
    const some = applies < total;
    return {
      ...item,
      // Relative words ("back to talking") are about ONE company's stage; a
      // selection gets the count instead, and only when it is not everyone.
      hint: some ? `${applies} of ${total}` : String(id).startsWith("move:") ? "" : item.hint,
      disabled: enabled === 0,
      why: enabled === 0 ? item.why : null,
    };
  });
  const sections = ["move", "hold", "end"]
    .map((sid) => ({
      id: sid,
      label: sid === "move" ? "Move to" : null,
      items: items.filter((i) => SECTION_OF(i.id) === sid),
    }))
    .filter((s) => s.items.length);
  return [...sections, person];
}

/*
 * Does this option apply to this bundle, for this choice? null when it does, or
 * why not, in a few words, for the confirm's "skipped" line.
 */
function skipWhy(id, choice, bundle) {
  const item = offeredBy(bundle).get(id);
  if (!item) {
    if (String(id).startsWith("move:") && bundle.company && bundle.company.status === "prospect" && `move:${bundle.company.pipeline_stage}` === id) {
      return "already there";
    }
    if (!bundle.company) return "no company";
    return STATUS_WORDS[bundle.company.status] || bundle.company.status;
  }
  // Closed lost greys out only when no reason is earned, which means no call.
  if (item.disabled) return id === "lost" ? "no call on record yet" : item.why || "not available";
  if (id === "lost" && choice && choice.reason) {
    const r = lostReasons(bundle.act && bundle.act.lost_evidence).find((x) => x.id === choice.reason);
    if (r && !r.enabled) return r.why;
  }
  return null;
}

/** Which bundles an option acts on, and which it skips with why. */
export function leadsPlan(id, choice, bundles) {
  const act = [];
  const skipped = [];
  for (const b of bundles || []) {
    const why = skipWhy(id, choice, b);
    if (why) skipped.push({ name: bundleName(b), why });
    else act.push(b);
  }
  return { act, skipped };
}

// "Foleon, Jamdesk and RNO1", or "Foleon, Jamdesk, RNO1 and 4 more".
function namesWords(names, max = 3) {
  if (names.length <= max) return listWords(names);
  return `${names.slice(0, max).join(", ")} and ${names.length - max} more`;
}

function skippedWords(skipped) {
  if (!skipped.length) return "";
  const byWhy = new Map();
  for (const s of skipped) {
    if (!byWhy.has(s.why)) byWhy.set(s.why, []);
    byWhy.get(s.why).push(s.name);
  }
  return ` Skips ${[...byWhy.entries()].map(([why, names]) => `${namesWords(names)} (${why})`).join("; ")}.`;
}

/** The park date "Remind me" resolves to, the record's way (endings.mjs remindDate). */
function remindUntil(choice, now) {
  if (!choice || choice.remind === "none") return null;
  if (choice.remind === "date") return choice.date ? dayOf(choice.date) : null;
  const months = Number(String(choice.remind || "6m").replace("m", "")) || 6;
  const d = new Date(toDate(now).getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

const PERSON_IDS = new Set(["not_a_lead"]);

/**
 * The confirm for an option. One row: the record's confirm, word for word. A
 * selection: the same fields and defaults, a counted title, and a sentence that
 * names who it changes and who it skips.
 */
export function leadsConfirmSpec(id, bundles, { now = new Date() } = {}) {
  const list = bundles || [];
  if (id === "delete") return deleteSpec(peopleIn(list));
  if (single(list)) {
    const b = list[0];
    return confirmSpec(id, { company: b.company, contact: b.contacts[0], act: b.act, now });
  }
  const first = list.find((b) => !skipWhy(id, null, b));
  if (!first) return null;
  const base = confirmSpec(id, { company: first.company, contact: first.contacts[0], act: first.act, now });
  if (!base) return null;

  const people = PERSON_IDS.has(id);
  const plan = (choice) => leadsPlan(id, choice, list);
  // Who it acts on for this choice, by name when it is one of them.
  const targets = (choice) => {
    const { act } = plan(choice);
    return people ? peopleIn(act).map((c) => fullName(c)) : act.map(bundleName);
  };
  const count = (choice) => {
    const t = targets(choice);
    if (t.length === 1) return t[0];
    return people ? plural(t.length, "person", "people") : plural(t.length, "company", "companies");
  };
  const many = (choice) => targets(choice).length !== 1;

  const spec = { ...base, title: titleFor(id, count(base.defaults || {}), many(base.defaults || {})) };
  if (id === "lost") {
    // A reason is offered when it is earned at one company at least; the
    // sentence then says which companies it skips.
    spec.reasons = base.reasons.map((r) => {
      const anywhere = list.some((b) => !skipWhy("lost", { reason: r.id }, b));
      return { ...r, enabled: anywhere, why: anywhere ? null : r.why };
    });
    spec.unearned = null;
    const pick = spec.reasons.find((r) => r.enabled);
    spec.defaults = { ...(base.defaults || {}), reason: pick ? pick.id : null };
  }
  spec.sentence = (c = {}) => {
    const t = targets(c);
    return `${bulkSentence(id, namesWords(t), t.length === 1, c, now)}${skippedWords(plan(c).skipped)}`;
  };
  spec.titleFor = (c = {}) => titleFor(id, count(c), many(c));
  return spec;
}

function titleFor(id, n, many) {
  if (String(id).startsWith("move:")) return `Move ${n} to ${STAGE_LABELS[String(id).slice(5)] || String(id).slice(5)}?`;
  switch (id) {
    case "won": return `Mark ${n} won?`;
    case "hold": return `Hold ${n}?`;
    case "not_now": return `Set ${n} aside until tomorrow?`;
    case "remove": return `Remove ${n} from the pipeline?`;
    case "lost": return `Close ${n} as lost?`;
    case "disqualify": return `Disqualify ${n}?`;
    case "vendor": return `Mark ${n} as ${many ? "vendors" : "a vendor"}?`;
    case "not_a_lead": return many ? `${n}: not leads?` : `${n} is not a lead?`;
    case "churn": return `Mark ${n} churned?`;
    default: return `${n}?`;
  }
}

// The consequence, in the record's words, for one company or several.
function bulkSentence(id, who, one, c, now) {
  const v = (single, many) => (one ? single : many);
  if (String(id).startsWith("move:")) {
    const label = STAGE_LABELS[String(id).slice(5)];
    return `${who} ${v("moves", "move")} to ${label}. Next follows the new stage, and the timeline shows the move as set by hand.`;
  }
  switch (id) {
    case "won": {
      const day = c.date ? dayOf(c.date) : now;
      return `${who} ${v("becomes a client", "become clients")}, won on ${longDate(day)}. The welcome and the finance intro come up in Next.`;
    }
    case "hold":
      return `${who} ${v("keeps its", "keep their")} stage and ${v("leaves", "leave")} your queues ${holdWords(c, now)}.`;
    case "not_now":
      return `${who} ${v("keeps its", "keep their")} stage and ${v("leaves", "leave")} your queues until tomorrow morning. Nothing is sent.`;
    case "remove": {
      const until = remindUntil(c, now);
      const why = c.reason ? `, with “${String(REASON_LABELS[c.reason] || c.reason).toLowerCase()}” as the reason` : "";
      const park = until ? `${v("parks", "park")} until ${longDate(until)}` : `${v("parks", "park")} with no reminder`;
      return `${who} ${v("leaves", "leave")} the board and ${park}${why}. ${v("It comes", "Each comes")} back by itself if they write.`;
    }
    case "lost": {
      const why = c.reason ? ` with “${String(REASON_LABELS[c.reason]).toLowerCase()}”` : "";
      return `${who} ${v("moves", "move")} to Closed lost${why}, and ${v("parks itself", "park themselves")} on ${longDate(addDays(now, LOST_TO_PARK_DAYS))}. Quotes and history stay.`;
    }
    case "disqualify":
      return `${who} ${v("leaves", "leave")} the board and ${v("is", "are")} never offered again.${c.reason === "junk" ? " The people you selected are marked junk too." : ""} ${v("Its timeline is", "Timelines are")} kept.`;
    case "vendor":
      return `${who} ${v("leaves", "leave")} the board and every report. ${v("Its timeline is", "Timelines are")} kept, and the people you selected stop counting as leads.`;
    case "not_a_lead":
      return `Deletes ${who} and ${v("their timeline", "their timelines")}, and ignores ${v("the address", "their addresses")} from then on: no contact, no email and no proposal, however often they write. A company goes with them if it holds nothing else. Kept as ${v("a receipt", "receipts")} under Ignored on the Leads page.`;
    case "churn": {
      const notice = c.notice ? dayOf(c.notice) : now;
      return `Service ends ${longDate(addDays(notice, 30))} for ${who}, 30 days after the notice, and that is the churn date.`;
    }
    default:
      return "";
  }
}

/**
 * DELETE…: the typo verb. Says what goes, that nothing is blocked afterwards
 * (the difference from Not a lead), and that it does not come back.
 */
export function deleteSpec(contacts) {
  const list = contacts || [];
  const one = list.length === 1 ? list[0] : null;
  const who = one ? fullName(one) || "this lead" : plural(list.length, "lead");
  return {
    title: `Delete ${who}?`,
    fields: [],
    defaults: {},
    confirmLabel: "Delete",
    sentence: () => {
      // With the address: a typo and its twin share a name, and the address is
      // the part that differs.
      const label = (c) => {
        const name = fullName(c) || "a lead";
        return c.email && c.email !== name ? `${name} (${c.email})` : name;
      };
      const named = one ? label(one) : namesWords(list.map(label));
      return (
        `Deletes ${named} and ${one ? "their timeline" : "their timelines"}, for a typo or a duplicate. ` +
        `A company goes too when nothing else is on it. ` +
        `Nothing is ignored: if ${one ? "that address" : "an address"} ever writes or fills in the form, it arrives as a new lead. This can't be undone.`
      );
    },
  };
}

function noteWrite(note, companyId, title) {
  const text = String(note || "").trim();
  return text && companyId ? [{ method: "POST", path: "/activities", body: { type: "note", company_id: companyId, contact_id: null, title, body: text } }] : [];
}

/** One bundle's writes, the record's (confirmWrites), with the person moves on the selected people. */
function bundleWrites(id, choice, b, now) {
  const co = b.company;
  const ctx = { company: co, contact: b.contacts[0], now };
  switch (id) {
    case "not_a_lead":
      return b.contacts.map((c) => ({ method: "POST", path: `/contacts/${c.id}/not-a-lead`, body: { note: String(choice.note || "").trim() || null } }));
    case "vendor":
      return [
        ...b.contacts.map((c) => ({ method: "POST", path: `/contacts/${c.id}/vendor`, body: {} })),
        ...noteWrite(choice.note, co && co.id, "Marked as vendor"),
      ];
    case "disqualify": {
      // The record junks the company's POC on "junk"; here it is the people picked.
      const out = writesFor(id, choice, { ...ctx, company: co && { ...co, primary_contact_id: null } });
      if (choice.reason === "junk") out.push(...b.contacts.map((c) => ({ method: "POST", path: `/contacts/${c.id}/junk`, body: {} })));
      return out;
    }
    default:
      return confirmWrites(id, choice, ctx);
  }
}

/**
 * Every write the confirm sends: [{ name, writes }] per company it acts on, in
 * order, and the companies it skips. Delete and Mark junk are per person.
 */
export function leadsWrites(id, choice, bundles, { now = new Date() } = {}) {
  const list = bundles || [];
  if (id === "delete" || id === "junk") {
    return {
      runs: peopleIn(list).map((c) => ({
        name: fullName(c) || "a lead",
        writes: [id === "delete" ? { method: "DELETE", path: `/contacts/${c.id}` } : { method: "POST", path: `/contacts/${c.id}/junk`, body: {} }],
      })),
      skipped: [],
    };
  }
  const { act, skipped } = single(list) ? { act: list, skipped: [] } : leadsPlan(id, choice, list);
  return { runs: act.map((b) => ({ name: bundleName(b), writes: bundleWrites(id, choice || {}, b, now) })), skipped };
}

